import type { VendorEvaluation, WorkspaceVendor } from "@/services/api";

// =============================================================================
// Glean Demo Constants
// =============================================================================

/** Fixed internal ID for the hardcoded Glean demo. Not a real Firestore ID. */
export const GLEAN_DEMO_EVALUATION_ID = "glean-demo";

/** Hardcoded workspace vendor entry shown to admins on the dashboard. */
export const GLEAN_DEMO_VENDOR: WorkspaceVendor = {
  vendor_id: "glean-demo-vendor",
  vendor_type: "public",
  vendor_name: "Glean",
  vendor_domain: "glean.com",
  gemini_file_id: null,
  has_evaluation: true,
  evaluation_status: "completed",
  evaluation_id: GLEAN_DEMO_EVALUATION_ID,
};

// =============================================================================
// Glean Mock Evaluation Data
// =============================================================================

export const GLEAN_MOCK_EVALUATION: VendorEvaluation = {
  id: "glean",
  vendor_id: "glean-vendor",
  vendor_name: "Glean",
  vendor_category: "Enterprise AI Search & Knowledge Management",
  evaluation_date: new Date().toISOString(),
  executive_summary: {
    key_takeaway:
      "Glean delivers exceptional enterprise search utility by connecting 100+ apps into a unified knowledge layer, but raises serious concerns around AI transparency, proprietary knowledge graph lock-in, and zero transferable learning for your organization.",
    sentiment: "neutral",
    paragraphs: [
      "Glean solves a genuine and costly enterprise problem. Organizations drowning in information silos across Slack, Drive, Confluence, Jira, and dozens of other tools will see immediate, measurable productivity gains. The product is polished, onboarding is frictionless, and the search quality is among the best in class.",
      "However, Glean's AI layer is entirely opaque. Users cannot see system prompts, understand how the knowledge graph prioritizes results, or select alternative models. The 'it just works' design philosophy trades organizational AI literacy for convenience—a decision with compounding long-term costs.",
      "Most critically, Glean's proprietary knowledge graph creates deep lock-in. The connections, enrichments, and organizational intelligence built inside Glean cannot be exported. The longer you use it, the harder it becomes to leave. Organizations should enter this relationship with eyes wide open about what they're trading away.",
    ],
  },
  criteria: [
    {
      criterion_id: "SEE",
      criterion_name: "SEE",
      subtitle: "Transparency & Visibility",
      score: "no",
      assessment:
        "Glean provides zero visibility into its AI operations. The knowledge graph construction process, ranking algorithms, and LLM prompt engineering are entirely hidden. Users cannot inspect how results are ranked, why certain documents are prioritized, or what model is generating AI answers. When asked during evaluation, Glean characterized these details as competitive advantages that cannot be disclosed.",
      tradeoff: {
        gain: "Clean, simple interface that requires no AI expertise to use",
        giveUp:
          "Any ability to verify AI decision-making; No organizational learning about how enterprise AI search works under the hood",
      },
      questions: [
        "Can enterprise customers get any visibility into how the knowledge graph ranks and retrieves results?",
        "What model(s) power Glean's AI answers, and how are prompts constructed?",
        "Is there a transparency roadmap for enterprise tier customers?",
      ],
      why_it_matters: [
        "Without visibility, you cannot verify that Glean's optimization choices serve your interests rather than their cost structure.",
        "Opaque AI prevents your team from developing any understanding of how enterprise search AI works.",
      ],
      sources: [
        {
          uri: "https://www.glean.com/product",
          title: "Glean Product Overview",
          snippet:
            "Glean uses deep learning models to understand your company's language, context, and relationships to deliver personalized results.",
        },
        {
          uri: "https://www.glean.com/security",
          title: "Glean Security",
          snippet:
            "Our proprietary AI models and knowledge graph technology are core intellectual property designed to deliver best-in-class enterprise search.",
        },
      ],
    },
    {
      criterion_id: "CHANGE",
      criterion_name: "CHANGE",
      subtitle: "Customizability & Control",
      score: "partially",
      assessment:
        "Glean allows administrators to configure data source priorities, create custom search scopes, and define organizational taxonomies. These are meaningful customizations. However, the core AI behavior—how answers are generated, how context windows are managed, which models are used—cannot be modified. You're accepting Glean's one-size-fits-all AI configuration for every use case in your organization.",
      tradeoff: {
        gain: "Useful administrative controls for search scope and data source weighting",
        giveUp:
          "Ability to tune AI behavior for specific departments, use cases, or sensitivity levels",
      },
      questions: [
        "Can different departments or teams have different AI behavior configurations?",
        "Is there an API for programmatic customization of search behavior?",
        "What customization capabilities are on the roadmap for the next 12 months?",
      ],
      why_it_matters: [
        "Legal, engineering, and sales teams have fundamentally different search needs.",
        "One-size-fits-all AI configurations inevitably underserve some use cases.",
      ],
      sources: [
        {
          uri: "https://www.glean.com/product/platform",
          title: "Glean Platform",
          snippet:
            "Administrators can configure data source connectors, set access permissions, and customize search relevance tuning for their organization.",
        },
      ],
    },
    {
      criterion_id: "ADAPT",
      criterion_name: "ADAPT",
      subtitle: "Vendor Agility & Innovation",
      score: "partially",
      assessment:
        "Glean has shipped significant product updates including AI assistant capabilities and generative answers. Their engineering team is clearly investing in staying current. However, model updates lag frontier releases by 3-6 months, and customers have no ability to independently adopt new models. Glean controls the pace of AI advancement for your organization.",
      tradeoff: {
        gain: "Vendor handles all model upgrades and testing—no migration burden",
        giveUp:
          "Ability to leverage cutting-edge AI capabilities when they become available; Dependence on Glean's prioritization decisions",
      },
      questions: [
        "What is your typical timeline for adopting new foundation models after release?",
        "Can enterprise customers opt into beta AI features early?",
      ],
      why_it_matters: [
        "AI is advancing at an extraordinary pace. A 6-month lag can mean missing significant capability improvements.",
        "Your competitors who manage their own AI stack can adopt new models immediately.",
      ],
      sources: [
        {
          uri: "https://www.glean.com/blog",
          title: "Glean Blog - Product Updates",
          snippet:
            "Introducing Glean AI Assistant: generative answers powered by the latest large language models, integrated directly into enterprise search.",
        },
      ],
    },
    {
      criterion_id: "USE",
      criterion_name: "USE",
      subtitle: "Genuine Utility & Value",
      score: "yes",
      assessment:
        "Glean delivers genuine, measurable value. The product connects to 100+ enterprise applications out of the box, and search quality is consistently strong across evaluated scenarios. Customer case studies report 2-4x improvements in information retrieval speed. The onboarding experience is smooth, and time-to-value is remarkably short. This is Glean's clear strength—it solves a real, expensive problem effectively.",
      tradeoff: {
        gain: "Best-in-class enterprise search that provides immediate, measurable productivity improvements across the organization",
        giveUp: "",
      },
      questions: [],
      why_it_matters: [
        "Enterprise knowledge fragmentation is a real, costly problem that Glean genuinely solves.",
        "The ROI on reduced search time is substantial and measurable.",
      ],
      sources: [
        {
          uri: "https://www.glean.com/customers",
          title: "Glean Customer Stories",
          snippet:
            "Databricks reduced time-to-information by 67% after deploying Glean across their 5,000+ person engineering organization.",
        },
        {
          uri: "https://www.glean.com/product/connectors",
          title: "Glean Connectors",
          snippet:
            "Connect to 100+ enterprise applications including Slack, Google Workspace, Microsoft 365, Confluence, Jira, Salesforce, and more.",
        },
      ],
    },
    {
      criterion_id: "LEARN",
      criterion_name: "LEARN",
      subtitle: "Transferable Knowledge",
      score: "no",
      assessment:
        "Using Glean teaches your team how to use Glean—nothing more. The search bar interface deliberately abstracts away all AI concepts. Users develop no understanding of retrieval-augmented generation, embedding models, context windows, or prompt engineering. This is convenient but strategically costly. Your workforce remains entirely dependent on Glean's specific interface rather than building transferable AI competency.",
      tradeoff: {
        gain: "Zero training overhead—anyone who can use Google can use Glean",
        giveUp:
          "Opportunity to build organizational AI literacy through daily tool usage; Workforce AI skills remain at zero regardless of usage duration",
      },
      questions: [],
      why_it_matters: [
        "Organizations that build AI fluency will outcompete those that outsource it entirely.",
        "Glean usage builds zero transferable skills—if you switch tools, you start from scratch.",
      ],
      sources: [
        {
          uri: "https://www.glean.com/product",
          title: "Glean Product",
          snippet:
            "Just type your question naturally. Glean's AI understands what you need and finds the answer instantly—no training required.",
        },
      ],
    },
    {
      criterion_id: "EXIT",
      criterion_name: "EXIT",
      subtitle: "Portability & Migration",
      score: "no",
      assessment:
        "Glean's knowledge graph—the connections, relationships, and enrichments built on top of your raw data—is entirely proprietary and cannot be exported. While your source documents remain in their original systems (Slack, Drive, etc.), the organizational intelligence layer that Glean builds is trapped. The longer and more extensively you use Glean, the more valuable this trapped layer becomes, and the more painful switching becomes. This is classic strategic lock-in.",
      tradeoff: {
        gain: "",
        giveUp:
          "All organizational knowledge enrichment is locked to Glean; Switching costs increase linearly with usage duration and depth",
      },
      questions: [
        "What exactly can be exported from Glean if we terminate the contract?",
        "Can the knowledge graph relationships be exported in any standard format?",
        "What does a typical customer migration away from Glean look like, and what is lost?",
        "Is data portability on your product roadmap?",
      ],
      why_it_matters: [
        "Lock-in is never in the customer's interest, especially in a market evolving this rapidly.",
        "The knowledge graph is where the real value accumulates—and it's the part you can't take with you.",
      ],
      sources: [
        {
          uri: "https://www.glean.com/security",
          title: "Glean Data Handling",
          snippet:
            "Glean builds a proprietary knowledge graph that maps relationships between people, content, and activity across your organization.",
        },
        {
          uri: "https://www.glean.com/terms",
          title: "Glean Terms of Service",
          snippet:
            "Upon contract termination, customer data access is maintained for 30 days. Knowledge graph data and derived insights are not exportable.",
        },
      ],
    },
  ],
  key_questions: [
    "How critical is unified enterprise search as an immediate pain point versus a problem you could solve incrementally?",
    "What is your organization's tolerance for vendor lock-in given how fast the AI search market is evolving?",
    "Does your team have other AI tools that build AI fluency, or would Glean be the primary AI touchpoint?",
    "How do you weigh the immediate productivity gains against the long-term strategic costs of opacity and lock-in?",
    "What is your 3-year plan for enterprise AI? Does Glean fit as a long-term partner or a bridge solution?",
    "Have you evaluated open-source or modular alternatives that offer more transparency and portability?",
  ],
  procurement_assessment: `## Competitive Analysis\nGlean operates in the enterprise AI search market alongside Coveo, Elastic, Microsoft Copilot, and several emerging startups. Its primary differentiation is its connector ecosystem (100+ integrations) and the unified knowledge graph across SaaS applications. However, Microsoft Copilot's bundling with M365 poses a significant competitive threat for shops already invested in the Microsoft ecosystem. Glean's pricing is premium relative to alternatives.\n\n## Security & Compliance\nGlean holds SOC 2 Type II, ISO 27001, and GDPR certifications. The company offers data residency in the US and EU. Glean does not store raw customer documents — it indexes metadata and generates embeddings. However, the vendor does not publicly disclose details about encryption key management (BYOK is not available on standard plans) or provide a machine-readable SBOM.\n\n## Performance & Quality\nPublished case studies report 2-4x improvement in knowledge discovery time. Glean's connector architecture ensures near-real-time indexing for most supported platforms. The AI answer quality is generally strong for factual lookups but can produce confidently incorrect synthesis when source documents conflict. No public SLA on answer accuracy or latency is provided.\n\n## Enterprise-Readiness\nGlean supports SAML 2.0 SSO, SCIM provisioning, RBAC, and inherits permissions from connected source systems. Audit logging is available on enterprise plans. The admin console provides governance controls for data source management and user analytics. However, document-level permission overrides and custom retention policies are limited.\n\n## Integration & Scalability\n100+ native connectors cover major enterprise SaaS platforms. A REST API and browser extensions extend reach. Glean has published references for deployments exceeding 25,000 users. However, the API is largely read-only — programmatic content ingestion and webhook-based workflows require enterprise-tier negotiation.\n\n## Other Procurement Concerns\n- **Vendor financial stability**: Glean has raised over $200M in funding and is valued at $4.6B. However, the company is pre-IPO and has not disclosed revenue or profitability metrics.\n- **Contract flexibility**: Annual contracts with auto-renewal are standard. Multi-year discounts are available but early termination is penalized.\n- **Data portability**: Knowledge graph enrichments and user interaction data cannot be exported. Source documents remain in their original systems.\n- **Support tiers**: Standard support is business-hours; premium 24/7 support requires additional spend.\n- **Reference customers**: Strong in technology, financial services, and professional services verticals. Limited published references in government, healthcare, or heavily regulated industries.`,
  status: "completed",
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

// =============================================================================
// Discovery Phase: Pre-Assessment Questions
// =============================================================================

export interface DiscoveryQuestion {
  id: string;
  message: string;
  options: string[];
}

export const DISCOVERY_QUESTIONS: DiscoveryQuestion[] = [
  {
    id: "budget",
    message:
      "I've reviewed the Glean evaluation report. Before I prepare your vendor call questions, let me understand your situation.\n\nWhat's your **budget range** for this type of tool?",
    options: [
      "Under $50k/year",
      "$50k–200k/year",
      "$200k+",
      "Still exploring",
    ],
  },
  {
    id: "priority",
    message: "Got it. And what matters most to you in this evaluation?",
    options: [
      "Data privacy & security",
      "AI transparency",
      "Integration flexibility",
      "Total cost of ownership",
      "Ease of adoption",
    ],
  },
  {
    id: "familiarity",
    message: "Last question — how familiar is your team with Glean already?",
    options: [
      "We've never used it",
      "We've seen a demo",
      "We're in a trial / POC",
      "We use it but evaluating alternatives",
    ],
  },
];

// =============================================================================
// Phase 2: Tailored Response Variants
// =============================================================================

export interface DiscoveryAnswers {
  budget: string;
  priority: string;
  familiarity: string;
}

interface TailoredResponse {
  issuesMessage: string;
  questionsMessage: string;
  alignmentMessage: string;
}

/**
 * Build a tailored response based on the user's discovery answers.
 * In a real product this would be AI-generated; here we select from
 * pre-written variants keyed by the "priority" answer (the most
 * differentiating signal) with budget/familiarity colour.
 */
export function buildTailoredResponse(answers: DiscoveryAnswers): TailoredResponse {
  // Issue ordering varies by priority
  const issuesByPriority: Record<string, string> = {
    "Data privacy & security": [
      "Based on your focus on **data privacy & security**, here are the 3 most critical issues I see:\n\n",
      "1. **Zero AI transparency** — Glean's AI pipeline is entirely opaque. You cannot inspect how your sensitive data is processed, what models handle it, or how context is constructed. For a security-conscious organization, this is a significant blind spot.\n\n",
      "2. **Knowledge graph lock-in** — Glean builds a proprietary intelligence layer on top of your data that cannot be exported. This creates a growing dependency that compounds over time.\n\n",
      "3. **No transferable AI skills** — Your team learns nothing about AI mechanics from using Glean, leaving you fully dependent on their black-box approach to data handling.",
    ].join(""),

    "AI transparency": [
      "Based on your focus on **AI transparency**, here are the 3 most critical issues I see:\n\n",
      "1. **Complete opacity** — Glean scored a hard **FAIL** on our SEE criterion. System prompts, model selection, ranking algorithms, and context handling are all hidden. Glean explicitly refused to disclose these during evaluation.\n\n",
      "2. **No organizational AI learning** — Because everything is abstracted away, your team builds zero understanding of how enterprise AI search works. This is a strategic blind spot.\n\n",
      "3. **Proprietary knowledge graph** — The enrichment layer Glean builds on your data is locked in. You can't inspect its logic or take it with you.",
    ].join(""),

    "Integration flexibility": [
      "Based on your focus on **integration flexibility**, here are the 3 most critical issues I see:\n\n",
      "1. **Proprietary knowledge graph** — While Glean connects to 100+ apps (a strength), the knowledge layer it builds is entirely proprietary. You cannot integrate its intelligence into other tools or workflows.\n\n",
      "2. **No API-level AI customization** — You can configure data sources but cannot control the AI behavior programmatically. This limits how deeply you can integrate Glean into custom workflows.\n\n",
      "3. **Exit difficulty** — If your integration needs evolve beyond what Glean supports, switching costs are high because the knowledge graph cannot be migrated.",
    ].join(""),

    "Total cost of ownership": [
      "Based on your focus on **total cost of ownership**, here are the 3 most critical issues I see:\n\n",
      "1. **Escalating switching costs** — Glean's proprietary knowledge graph means the longer you use it, the more expensive it becomes to leave. This is a hidden cost that compounds annually.\n\n",
      "2. **No AI skill building** — Your team learns nothing transferable from Glean. If you ever switch tools, you're starting from zero — that retraining cost should be factored in.\n\n",
      "3. **Opaque AI operations** — You cannot verify whether Glean's AI choices optimize for your outcomes or their cost structure. Without transparency, TCO analysis is incomplete.",
    ].join(""),

    "Ease of adoption": [
      "Good news — ease of adoption is Glean's strongest suit. But here are the 3 issues to watch:\n\n",
      "1. **The convenience trap** — Glean's frictionless onboarding is genuinely excellent, but it comes at the cost of organizational AI literacy. Your team learns to use Glean, not to understand AI.\n\n",
      "2. **Knowledge graph lock-in** — The easier adoption is, the faster your organization becomes dependent. Glean's proprietary knowledge layer cannot be exported.\n\n",
      "3. **AI opacity** — The 'it just works' design means zero visibility into AI decision-making. Convenient today, but a strategic liability as AI becomes more central to operations.",
    ].join(""),
  };

  // Budget-aware question adjustments
  const budgetContext =
    answers.budget === "$200k+"
      ? "Given your significant budget, you have leverage to negotiate transparency provisions"
      : answers.budget === "Still exploring"
        ? "Since you're still exploring budget"
        : "Given your budget range";

  // Familiarity-aware intro for questions
  const familiarityContext =
    answers.familiarity === "We've never used it"
      ? "Since you haven't used Glean yet, focus on getting concrete answers before committing"
      : answers.familiarity === "We're in a trial / POC"
        ? "Since you're already in a trial, you're in a strong position to test these directly"
        : answers.familiarity === "We use it but evaluating alternatives"
          ? "Since you're already a customer, you can validate these issues against your real experience"
          : "Since you've seen a demo, push beyond the polished surface";

  const issuesMessage =
    issuesByPriority[answers.priority] || issuesByPriority["AI transparency"];

  const questionsMessage = [
    `${budgetContext}, and ${familiarityContext.toLowerCase()}, here are the questions I'd prioritize for your vendor call:\n\n`,
    "1. **\"What visibility can we get into how Glean's AI ranks and retrieves results?\"** — Push for specifics, not marketing answers.\n\n",
    "2. **\"If we terminate in 2 years, what exactly can we export? What is lost?\"** — Ask for a concrete migration scenario.\n\n",
    "3. **\"What models power your AI answers, and what is your update cadence?\"** — Benchmark against their competitors.\n\n",
    "4. **\"Can different teams have different AI configurations?\"** — Test whether one-size-fits-all is truly the only option.\n\n",
    answers.budget === "$200k+" || answers.budget === "$50k–200k/year"
      ? "5. **\"Can we negotiate a contractual right to knowledge graph export in standard formats?\"** — Use your budget as leverage for better terms."
      : "5. **\"What does a typical customer migration away from Glean look like?\"** — Understand the real exit cost before you're locked in.",
  ].join("");

  const alignmentMessage =
    "Does this look right? Feel free to ask me anything else about the report, or if you're ready — let's plan your vendor call.";

  return { issuesMessage, questionsMessage, alignmentMessage };
}

// =============================================================================
// Glean Vendor Call Questions (pre-populated by AI Assistant)
// =============================================================================

export interface VendorCallQuestion {
  id: string;
  category: string;
  question: string;
  aiSuggested: boolean;
  /** Optional context note shown below the question */
  context?: string;
  /** Framework criterion this question relates to */
  criterion_id?: string;
}

/**
 * Questions the AI assistant proposes for the Glean vendor call.
 * These are shown pre-populated when the user navigates to /vendor-call?vendor=glean-demo.
 */
export const GLEAN_VENDOR_CALL_QUESTIONS: VendorCallQuestion[] = [
  // AI Transparency (from SEE criterion - FAIL)
  {
    id: "glean-t1",
    category: "AI Transparency",
    question: "What visibility can we get into how Glean's AI ranks and retrieves results?",
    aiSuggested: true,
    context: "SEE criterion scored FAIL — Glean refused to disclose AI pipeline details during evaluation.",
    criterion_id: "SEE",
  },
  {
    id: "glean-t2",
    category: "AI Transparency",
    question: "What models power your AI answers, and what is your update cadence for adopting new models?",
    aiSuggested: true,
    context: "Model updates lag frontier releases by 3-6 months per evaluation findings.",
    criterion_id: "SEE",
  },
  {
    id: "glean-t3",
    category: "AI Transparency",
    question: "How are system prompts constructed, and can enterprise customers inspect or influence them?",
    aiSuggested: true,
    criterion_id: "SEE",
  },
  {
    id: "glean-t4",
    category: "AI Transparency",
    question: "Is there a transparency roadmap? Will enterprise customers ever get access to AI decision-making details?",
    aiSuggested: true,
    criterion_id: "SEE",
  },

  // Data & Portability (from EXIT criterion - FAIL)
  {
    id: "glean-d1",
    category: "Data & Portability",
    question: "If we terminate in 2 years, what exactly can we export? What is lost?",
    aiSuggested: true,
    context: "EXIT criterion scored FAIL — Knowledge graph is proprietary and non-exportable.",
    criterion_id: "EXIT",
  },
  {
    id: "glean-d2",
    category: "Data & Portability",
    question: "Can the knowledge graph relationships be exported in any standard format (RDF, JSON-LD, etc.)?",
    aiSuggested: true,
    criterion_id: "EXIT",
  },
  {
    id: "glean-d3",
    category: "Data & Portability",
    question: "What does a typical customer migration away from Glean look like? Can you share a reference?",
    aiSuggested: true,
    criterion_id: "EXIT",
  },
  {
    id: "glean-d4",
    category: "Data & Portability",
    question: "Is data portability on your product roadmap? What timeline?",
    aiSuggested: true,
    criterion_id: "EXIT",
  },

  // Customization & Control (from CHANGE criterion - PARTIAL)
  {
    id: "glean-c1",
    category: "Customization",
    question: "Can different departments or teams have different AI behavior configurations?",
    aiSuggested: true,
    context: "CHANGE criterion scored PARTIAL — Admin controls exist but core AI layer is not configurable.",
    criterion_id: "CHANGE",
  },
  {
    id: "glean-c2",
    category: "Customization",
    question: "Is there an API for programmatic customization of search behavior and ranking?",
    aiSuggested: true,
    criterion_id: "CHANGE",
  },
  {
    id: "glean-c3",
    category: "Customization",
    question: "What customization capabilities are on the roadmap for the next 12 months?",
    aiSuggested: true,
    criterion_id: "CHANGE",
  },

  // Commercial & Contract (based on budget discovery)
  {
    id: "glean-p1",
    category: "Commercial",
    question: "Can we negotiate a contractual right to knowledge graph export in standard formats?",
    aiSuggested: true,
    context: "Use your budget as leverage to secure better data portability terms.",
  },
  {
    id: "glean-p2",
    category: "Commercial",
    question: "What is your pricing model per seat, and are there volume discounts above 500 users?",
    aiSuggested: true,
  },
  {
    id: "glean-p3",
    category: "Commercial",
    question: "What are the contract termination terms and notice periods?",
    aiSuggested: true,
  },

  // Strategic Fit (from LEARN criterion - FAIL)
  {
    id: "glean-s1",
    category: "Strategic Fit",
    question: "How does Glean help our team build transferable AI skills, beyond learning the Glean interface?",
    aiSuggested: true,
    context: "LEARN criterion scored FAIL — Using Glean teaches nothing about AI mechanics.",
    criterion_id: "LEARN",
  },
  {
    id: "glean-s2",
    category: "Strategic Fit",
    question: "Do you offer any advanced/power-user modes that expose more of the AI mechanics?",
    aiSuggested: true,
    criterion_id: "LEARN",
  },
  {
    id: "glean-s3",
    category: "Strategic Fit",
    question: "What is your 3-year product vision? How do you see Glean evolving as a platform?",
    aiSuggested: true,
    criterion_id: "LEARN",
  },
];

export const GLEAN_VENDOR_CALL_CATEGORIES = [
  "AI Transparency",
  "Data & Portability",
  "Customization",
  "Commercial",
  "Strategic Fit",
];

// =============================================================================
// Generic Assistant Responses (non-Glean pages)
// =============================================================================

export const GENERIC_INITIAL_MESSAGE =
  "Hi! I'm your FeedForward assistant. I can help you navigate the platform, understand evaluation reports, or prepare for vendor calls. What can I help you with?";

export const GENERIC_RESPONSES: Record<string, string> = {
  default:
    "I can help you with evaluations, reports, and vendor call preparation. Try navigating to a vendor report — I'll have much more to say when I can see the evaluation data!",
  help: "Here's what I can do:\n\n• **On report pages** — I'll analyze the evaluation and help you prepare vendor call questions\n• **On the dashboard** — I can help you find evaluations and navigate\n• **Anywhere** — Ask me about the FeedForward framework (SEE, CHANGE, ADAPT, USE, LEARN, EXIT)",
  framework:
    "The FeedForward framework evaluates AI vendors on 6 criteria:\n\n• **SEE** — Transparency & Visibility\n• **CHANGE** — Customizability & Control\n• **ADAPT** — Vendor Agility & Innovation\n• **USE** — Genuine Utility & Value\n• **LEARN** — Transferable Knowledge\n• **EXIT** — Portability & Migration\n\nEach criterion is scored as Pass, Partial, or Fail.",
};
